import * as Phaser from 'phaser';
import { PaintedScene } from '../PaintedScene';
import {
  installSnowTexture,
  installBreathTexture,
  createSnowfall,
  createBreathMist,
} from './frostmereAmbient';
import {
  preloadVariantLayers,
  buildVariantSwitcher,
  readFrostmereRestoration,
  getRestorationTier,
  type VariantSwitcher,
  type RestorationTier,
  FROSTMERE_RESTORATION_KEY,
} from './frostmereRestoration';

export const THAW_LINE_PHASER_KEY = 'ThawLineScene' as const;
export const THAW_LINE_MANIFEST_KEY = 's-307-thaw-line-ridge' as const;

const DRIP_TEXTURE_KEY = '__thaw_line_drip__';

const VARIANT_CONFIG = {
  tiers: [0, 50] as RestorationTier[],
  layerPaths: {
    0: {
      'sky': 'assets/scenes/frostmere/S-307-0pct/layer-sky.png',
      'far-bg': 'assets/scenes/frostmere/S-307-0pct/layer-far.png',
      'mid': 'assets/scenes/frostmere/S-307-0pct/layer-mid.png',
      'fg': 'assets/scenes/frostmere/S-307-0pct/layer-fg.png',
    },
    50: {
      'sky': 'assets/scenes/frostmere/S-307/layer-sky.png',
      'far-bg': 'assets/scenes/frostmere/S-307/layer-far.png',
      'mid': 'assets/scenes/frostmere/S-307/layer-mid.png',
      'fg': 'assets/scenes/frostmere/S-307/layer-fg.png',
    },
  },
};

export class ThawLineScene extends PaintedScene {
  private snowEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private breathEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private dripEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private variantSwitcher: VariantSwitcher | null = null;

  constructor() {
    super(THAW_LINE_PHASER_KEY, THAW_LINE_MANIFEST_KEY);
  }

  preload(): void {
    super.preload();
    installSnowTexture(this);
    installBreathTexture(this);
    this.installDripTexture();
    preloadVariantLayers(this, 's-307', VARIANT_CONFIG);
  }

  protected override onSceneReady(): void {
    this.variantSwitcher = buildVariantSwitcher(this, 's-307', VARIANT_CONFIG);
    this.snowEmitter = createSnowfall(this, 0.6);

    if (this.walker) {
      this.breathEmitter = createBreathMist(this, this.walker);
    }

    const pct = readFrostmereRestoration(this.game);
    if (getRestorationTier(pct) >= 50) {
      this.buildDripEffect();
    }

    this.game.registry.events.on(
      `changedata-${FROSTMERE_RESTORATION_KEY}`,
      this.onRestorationChange,
      this,
    );
  }

  protected override onSceneShutdown(): void {
    this.snowEmitter?.destroy();
    this.breathEmitter?.destroy();
    this.dripEmitter?.destroy();
    this.variantSwitcher?.destroy();
    this.game.registry.events.off(
      `changedata-${FROSTMERE_RESTORATION_KEY}`,
      this.onRestorationChange,
      this,
    );
    this.snowEmitter = null;
    this.breathEmitter = null;
    this.dripEmitter = null;
    this.variantSwitcher = null;
  }

  private onRestorationChange = (_parent: unknown, value: number): void => {
    if (getRestorationTier(value) >= 50 && !this.dripEmitter) {
      this.buildDripEffect();
    }
  };

  private installDripTexture(): void {
    if (this.textures.exists(DRIP_TEXTURE_KEY)) return;
    const gfx = this.make.graphics({ x: 0, y: 0 }, false);
    gfx.fillStyle(0x80b8d0, 0.7);
    gfx.fillRect(0, 0, 2, 5);
    gfx.generateTexture(DRIP_TEXTURE_KEY, 2, 5);
    gfx.destroy();
  }

  private buildDripEffect(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    this.dripEmitter = this.add.particles(0, 0, DRIP_TEXTURE_KEY, {
      x: { min: w * 0.1, max: w * 0.9 },
      y: h * 0.5,
      speedY: { min: 40, max: 80 },
      speedX: { min: -2, max: 2 },
      alpha: { start: 0.6, end: 0 },
      scale: { min: 0.6, max: 1.2 },
      lifespan: { min: 1200, max: 2000 },
      frequency: 200,
      quantity: 1,
      maxAliveParticles: 15,
    });
    this.dripEmitter.setScrollFactor(0);
    this.dripEmitter.setDepth(4);
  }
}
